  if (!customElements.get('custom-popup-modal')) {
    class CustomPopupModal extends HTMLElement {
      constructor() {
        super();
        this.modal = this.querySelector('.cp-modal');
        this.closeButtons = this.querySelectorAll('[data-close-popup]');
        
        // Bind methods to maintain 'this' context
        this.open = this.open.bind(this);
        this.close = this.close.bind(this);
        this.handleGlobalClick = this.handleGlobalClick.bind(this);
        this.handleEscape = this.handleEscape.bind(this);

        this.init();
      }

      init() {
        // 1. Close Button logic
        this.closeButtons.forEach(btn => btn.addEventListener('click', this.close));
        
        // 2. Click outside modal box to close
        this.modal.addEventListener('click', (e) => {
          if (e.target === this.modal) this.close();
        });

        // 3. Escape key to close
        document.addEventListener('keydown', this.handleEscape);

        // 4. Global CTA Hijacker (Captures clicks anywhere on the page)
        document.addEventListener('click', this.handleGlobalClick);

        // Shopify reloads the page after a native contact submission.
        // Reopen this modal only when its own form returned a result.
        if (this.querySelector('.cp-success-msg, .cp-error-msg')) {
          this.open();
        }
      }

      get isOpen() {
        return this.modal.classList.contains('is-active');
      }

      open(e) {
        if (e) e.preventDefault();
        this.modal.classList.add('is-active');
        this.modal.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden'; // Prevents background scrolling
      }

      close() {
        this.modal.classList.remove('is-active');
        this.modal.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = ''; // Restores background scrolling
      }

      handleEscape(e) {
        if (e.key === 'Escape' && this.isOpen) {
          this.close();
        }
      }

      handleGlobalClick(e) {
        const target = e.target;
        
        // Identify valid CTA targets (ends with #myform or #footerform)
        const cta = target.closest('a[href$="#myform"], a[href$="#footerform"]');
        
        if (!cta) return;

        // SAFEGUARD: Do not hijack core Shopify actions (Cart, Checkout, generic Submits)
        if (cta.type === 'submit' || cta.closest('form[action^="/cart"]') || cta.name === 'add' || cta.name === 'checkout') {
          return;
        }

        // Scroll to Footer Form
        if (cta.getAttribute('href').endsWith('#footerform')) {
          e.preventDefault();
          const footerForm = document.querySelector('.fc-footer-form-wrapper') || document.querySelector('.footer-block--form');
          if (footerForm) {
            footerForm.scrollIntoView({ behavior: 'smooth', block: 'center' });
            
            // Focus the first input or textarea after scrolling
            const firstInput = footerForm.querySelector('input:not([type="hidden"]), textarea');
            if (firstInput) {
              setTimeout(() => firstInput.focus(), 600);
            }
          }
          return;
        }

        // Explicit link match for Popup
        if (cta.getAttribute('href').endsWith('#myform')) {
          this.open(e);
          return;
        }

        // Broad word-matching logic (only applies if link ended in #myform or #footerform anyway due to selector)
        const text = cta.innerText.toLowerCase();
        const triggerWords = ['meet', 'contact', 'fill', 'get ', 'start', 'auditing', 'team', 'learn'];
        
        if (triggerWords.some(word => text.includes(word))) {
          this.open(e);
        }
      }
    }

    // Register Web Component
    customElements.define('custom-popup-modal', CustomPopupModal);
  }

  // Contact forms are sent in the background so the visitor stays on the page and gets a
  // notification. Requires hCaptcha to be off for contact forms (Online Store > Preferences),
  // otherwise Shopify rejects the request for a missing captcha token.
  {
    const formSelector = '#PopupContactForm, #ContactFooterInline';
    const errorText = 'Sorry, your message could not be sent. Please try again.';

    const setLoading = (form, loading) => {
      const button = form.querySelector('[type="submit"]');
      if (!button) return;
      if (loading && !button.classList.contains('is-loading')) {
        button.dataset.label = button.textContent.trim();
        button.innerHTML = '<span class="form-spinner" aria-hidden="true"></span>Sending…';
      } else if (!loading && button.classList.contains('is-loading')) {
        button.textContent = button.dataset.label;
      }
      button.classList.toggle('is-loading', loading);
      button.setAttribute('aria-busy', loading);
    };

    const showToast = (message, type) => {
      let toast = document.getElementById('form-toast');
      if (!toast) {
        toast = Object.assign(document.createElement('div'), { id: 'form-toast', className: 'form-toast' });
        toast.setAttribute('role', 'status');
        toast.setAttribute('aria-live', 'polite');
        document.body.appendChild(toast);
      }
      toast.textContent = message;
      toast.className = `form-toast form-toast--${type} is-visible`;
      clearTimeout(toast.hideTimer);
      toast.hideTimer = setTimeout(() => toast.classList.remove('is-visible'), 5000);
    };

    const sendInBackground = async (form) => {
      try {
        // Shopify redirects after both success and errors; only a successful submission
        // lands on a URL with contact_posted=true.
        const response = await fetch(form.action, {
          method: 'POST',
          body: new FormData(form),
          credentials: 'same-origin',
          headers: { Accept: 'text/html' },
        });

        if (new URL(response.url).searchParams.get('contact_posted') === 'true') {
          response.body?.cancel();
          const message = form.querySelector('[data-success-message]')?.textContent.trim();
          form.reset();
          form.closest('custom-popup-modal')?.close();
          showToast(message || 'Thank you! Your message has been sent.', 'success');
        } else {
          const page = new DOMParser().parseFromString(await response.text(), 'text/html');
          const error = page.querySelector(`#${form.id} .fc-form-error, #${form.id} .cp-error-msg`);
          showToast(error?.textContent.replace(/\s+/g, ' ').trim() || errorText, 'error');
        }
      } catch (err) {
        console.error('Contact form:', err);
        showToast(errorText, 'error');
      } finally {
        setLoading(form, false);
      }
    };

    document.addEventListener('submit', (event) => {
      const form = event.target.closest?.(formSelector);
      if (!form) return;
      event.preventDefault();
      setLoading(form, true);
      sendInBackground(form);
    });

    // Highlight the pre-filled default message when the box is reached with the keyboard, so
    // typing replaces it. Mouse and touch focus are left alone so text can be selected by hand.
    let pointerFocus = false;
    document.addEventListener('pointerdown', () => { pointerFocus = true; }, true);
    document.addEventListener('keydown', () => { pointerFocus = false; }, true);

    document.addEventListener('focusin', (event) => {
      const box = event.target;
      if (pointerFocus || !box.matches?.('textarea[name="contact[body]"]') || !box.closest(formSelector)) return;
      if (box.value !== box.defaultValue) return;
      box.select();
    });
  }
